import { v4 as uuid } from 'uuid';
import { FundingPolicyType } from '@shared';

export type CcrcCoverageType = 'single' | 'couple';

export interface CcrcTypeAPricingBreakdown {
  monthlyPerPerson: number;
  primaryEntranceFee: number;
  partnerEntranceFee: number | null;
  totalEntranceFee: number;
  totalMonthlyFee: number;
}

export function lookupPricingTier(
  product: CcrcTypeAProduct,
  age: number,
): CcrcTypeAPricingTier | undefined {
  if (product.pricing.length === 0) {
    return undefined;
  }

  const clampedAge = Math.min(Math.max(age, product.minAge), product.maxAge);
  const byAge = [...product.pricing].sort((a, b) => a.age - b.age);

  return byAge.find(tier => tier.age === clampedAge) ?? byAge[byAge.length - 1];
}

export function lookupEntranceFee(
  product: CcrcTypeAProduct,
  coverage: CcrcCoverageType,
  age: number,
): number {
  const tier = lookupPricingTier(product, age);

  if (!tier) {
    return 0;
  }

  return coverage === 'single' ? tier.singleEntranceFee : tier.coupleEntranceFeePerPerson;
}

export function monthlyFeePerPerson(product: CcrcTypeAProduct, coverage: CcrcCoverageType): number {
  return coverage === 'single' ? product.monthlySingleFee : product.monthlyCoupleFeePerPerson;
}

export function computeCcrcPricing(args: {
  product: CcrcTypeAProduct;
  coverage: CcrcCoverageType;
  clientAge: number;
  partnerAge?: number;
  hasPartner: boolean;
}): CcrcTypeAPricingBreakdown {
  const { product, coverage, clientAge, partnerAge, hasPartner } = args;
  const monthlyPerPerson = monthlyFeePerPerson(product, coverage);
  const primaryEntranceFee = lookupEntranceFee(product, coverage, clientAge);

  if (coverage === 'couple' && hasPartner && partnerAge != null) {
    const partnerEntranceFee = lookupEntranceFee(product, coverage, partnerAge);

    return {
      monthlyPerPerson,
      primaryEntranceFee,
      partnerEntranceFee,
      totalEntranceFee: primaryEntranceFee + partnerEntranceFee,
      totalMonthlyFee: monthlyPerPerson * 2,
    };
  }

  return {
    monthlyPerPerson,
    primaryEntranceFee,
    partnerEntranceFee: null,
    totalEntranceFee: primaryEntranceFee,
    totalMonthlyFee: monthlyPerPerson,
  };
}

interface BuildPolicyArgs {
  product: CcrcTypeAProduct;
  entranceFee: number;
  monthlyFee: number;
  addedByAdvisorId?: string;
}

export function buildCcrcTypeAPolicy({
  product,
  entranceFee,
  monthlyFee,
  addedByAdvisorId,
}: BuildPolicyArgs): PolicyFundingSource {
  return {
    policyType: product.fundingPolicyType as FundingPolicyType,
    hasPolicyFunding: true,
    policyCustomName: product.productName,
    policyLumpSumPayment: entranceFee,
    policyPremiumMonthlyCost: monthlyFee,
    policyPremiumStartYear: new Date().getFullYear(),
    policyPremiumIncreaseRate: product.annualIncreaseRate,
    policyPremiumIncreaseYears: null,
    policyBenefitUtilizedToDate: 0,
    isIndemnityPolicyPayment: false,
    isSimpleInflationProtection: false,
    addedByAdvisorId,
    policyAddedAt: Date.now(),
  } as PolicyFundingSource;
}

export function buildCcrcTypeAPendingPolicy(
  args: BuildPolicyArgs & { guidedPlanningSessionId: string },
): PendingPolicyFundingSource {
  return {
    ...buildCcrcTypeAPolicy(args),
    guidedPlanningSessionId: args.guidedPlanningSessionId,
    pendingFundingSourceId: uuid(),
  } as PendingPolicyFundingSource;
}

interface BuildManualPolicyArgs {
  productName: string;
  entranceFee: number;
  monthlyFee: number;
  startYear: number;
  annualIncreaseRate: number;
  addedByAdvisorId?: string;
}

export function buildManualCcrcTypeAPolicy({
  productName,
  entranceFee,
  monthlyFee,
  startYear,
  annualIncreaseRate,
  addedByAdvisorId,
}: BuildManualPolicyArgs): PolicyFundingSource {
  return {
    policyType: FundingPolicyType.fortifiedLife,
    hasPolicyFunding: true,
    isLifePlanningCommunityProduct: true,
    policyCustomName: productName,
    policyLumpSumPayment: entranceFee,
    policyPremiumMonthlyCost: monthlyFee,
    policyPremiumStartYear: startYear,
    policyPremiumIncreaseRate: annualIncreaseRate,
    policyPremiumIncreaseYears: null,
    policyBenefitUtilizedToDate: 0,
    isIndemnityPolicyPayment: false,
    isSimpleInflationProtection: false,
    addedByAdvisorId,
    policyAddedAt: Date.now(),
  } as PolicyFundingSource;
}
